if (!customElements.get('social-videos')) {
  let youtubeAPI;

  function loadYouTubeAPI() {
    if (window.YT?.Player) return Promise.resolve(window.YT);
    if (youtubeAPI) return youtubeAPI;
    youtubeAPI = new Promise((resolve, reject) => {
      const previous = window.onYouTubeIframeAPIReady;
      const timeout = setTimeout(() => { youtubeAPI = null; reject(new Error('YouTube API unavailable')); }, 15000);
      window.onYouTubeIframeAPIReady = () => {
        clearTimeout(timeout);
        resolve(window.YT);
        if (typeof previous === 'function') previous();
      };
      if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
        const script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';
        script.async = true;
        script.onerror = () => { clearTimeout(timeout); youtubeAPI = null; reject(new Error('YouTube API unavailable')); };
        document.head.append(script);
      }
    });
    return youtubeAPI;
  }

  function youtubeID(value) {
    try {
      const url = new URL(value);
      if (!['https:', 'http:'].includes(url.protocol)) return null;
      const host = url.hostname.toLowerCase().replace(/^www\./, '');
      let id;
      if (host === 'youtu.be') id = url.pathname.split('/')[1];
      else if (['youtube.com', 'm.youtube.com', 'youtube-nocookie.com'].includes(host)) {
        id = url.pathname === '/watch' ? url.searchParams.get('v') : /^\/(shorts|embed|live)\//.test(url.pathname) ? url.pathname.split('/')[2] : null;
      }
      return /^[\w-]{11}$/.test(id || '') ? id : null;
    } catch { return null; }
  }

  class YouTubeVideo {
    constructor(element, section) {
      this.element = element;
      this.section = section;
      this.dataset = element.dataset;
      this.id = youtubeID(this.dataset.youtubeUrl);
      this.paused = true;
      this._muted = section.muted;
      this.destroyed = false;
      if (!this.id) this.fail();
      else {
        const poster = element.querySelector('svg.social-videos__youtube-poster');
        if (poster) {
          const image = document.createElement('img');
          image.className = 'social-videos__youtube-poster';
          image.src = `https://i.ytimg.com/vi/${this.id}/hqdefault.jpg`;
          image.alt = '';
          image.width = 480;
          image.height = 360;
          image.loading = 'lazy';
          poster.replaceWith(image);
        }
      }
    }

    get parentElement() { return this.element.parentElement; }
    get muted() { return this._muted; }
    set muted(value) {
      this._muted = value;
      if (this.ready) value ? this.player.mute() : this.player.unMute();
    }

    fail() {
      this.paused = true;
      this.wantsPlay = false;
      this.element.classList.add('has-error');
      this.element.querySelector('.social-videos__youtube-fallback').hidden = false;
      this.section.updatePlayButton(this);
    }

    async play() {
      if (!this.id || this.destroyed) return;
      this.wantsPlay = true;
      try {
        if (!this.loading) this.loading = this.initialize();
        await this.loading;
        if (!this.wantsPlay || this.destroyed) return;
        this.muted = this.section.muted;
        this.player.playVideo();
      } catch { if (!this.destroyed) this.fail(); }
    }

    async initialize() {
      const YT = await loadYouTubeAPI();
      if (this.destroyed) return;
      await new Promise((resolve, reject) => {
        this.readyTimeout = setTimeout(() => reject(new Error('YouTube player unavailable')), 15000);
        this.player = new YT.Player(this.element.querySelector('[data-youtube-mount]'), {
          width: '100%', height: '100%', videoId: this.id,
          playerVars: { controls: 0, playsinline: 1, loop: 1, playlist: this.id, rel: 0, origin: location.origin },
          events: {
            onReady: () => {
              clearTimeout(this.readyTimeout);
              if (this.destroyed) return;
              this.ready = true;
              this.muted = this.section.muted;
              const iframe = this.player.getIframe();
              iframe.title = this.dataset.title;
              iframe.tabIndex = -1;
              resolve();
            },
            onStateChange: ({ data }) => {
              if (this.destroyed) return;
              if (data === YT.PlayerState.PLAYING && !this.wantsPlay) { this.player.pauseVideo(); return; }
              this.paused = data !== YT.PlayerState.PLAYING && data !== YT.PlayerState.BUFFERING;
              if (data === YT.PlayerState.PLAYING) this.element.classList.add('is-loaded');
              this.section.updatePlayButton(this);
            },
            onAutoplayBlocked: () => { this.paused = true; this.section.updatePlayButton(this); },
            onError: () => { clearTimeout(this.readyTimeout); reject(new Error('YouTube embed unavailable')); this.fail(); }
          }
        });
      });
    }

    pause() {
      this.wantsPlay = false;
      if (this.ready) this.player.pauseVideo();
      this.paused = true;
      this.section.updatePlayButton(this);
    }

    destroy() {
      this.destroyed = true;
      this.wantsPlay = false;
      clearTimeout(this.readyTimeout);
      this.player?.destroy();
      this.element.classList.remove('is-loaded', 'has-error');
      this.element.querySelector('.social-videos__youtube-fallback').hidden = true;
      if (!this.element.querySelector('[data-youtube-mount]')) {
        const mount = document.createElement('div');
        mount.setAttribute('data-youtube-mount', '');
        this.element.append(mount);
      }
    }
  }

  customElements.define('social-videos', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.social-videos__track');
      this.querySelectorAll('[data-loop-copy]').forEach((slide) => slide.remove());
      this.originals = [...this.querySelectorAll('.social-videos__slide')];
      this.slides = this.originals;
      if (!this.slides.length) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = false;
      this.active = -1;
      this.muted ??= true;
      this.youtubeVideos = new Map();
      this.buildLoop();
      this.track.addEventListener('scroll', () => {
        this.schedule();
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => this.recenter(), 160);
      }, { ...options, passive: true });
      this.addEventListener('click', (event) => this.handleClick(event), options);
      this.track.addEventListener('keydown', (event) => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.goTo(this.active + (event.key === 'ArrowRight' ? 1 : -1));
      }, options);
      this.track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('[data-mute], a')) return;
        this.drag = { x: event.clientX, scroll: this.track.scrollLeft, moved: false };
      }, options);
      this.track.addEventListener('pointermove', (event) => {
        if (!this.drag) return;
        const distance = event.clientX - this.drag.x;
        if (Math.abs(distance) > 5) this.drag.moved = true;
        if (!this.drag.moved) return;
        this.track.setPointerCapture(event.pointerId);
        this.track.classList.add('is-dragging');
        this.track.scrollLeft = this.drag.scroll - distance;
      }, options);
      const endDrag = () => {
        if (!this.drag) return;
        const moved = this.drag.moved;
        this.suppressClick = moved;
        this.drag = null;
        this.track.classList.remove('is-dragging');
        if (moved) { this.update(); this.goTo(this.active); }
      };
      this.track.addEventListener('pointerup', endDrag, options);
      this.track.addEventListener('pointercancel', endDrag, options);
      this.addEventListener('play', (event) => this.updatePlayButton(event.target), { ...options, capture: true });
      this.addEventListener('pause', (event) => this.updatePlayButton(event.target), { ...options, capture: true });
      this.addEventListener('volumechange', (event) => this.updateMuteButton(event.target), { ...options, capture: true });
      this.slides.forEach((slide) => {
        const video = this.getMedia(slide);
        if (!video) return;
        video.muted = this.muted;
        this.updatePlayButton(video);
        this.updateMuteButton(video);
      });
      document.addEventListener('visibilitychange', () => this.syncPlayback(), options);
      document.addEventListener('shopify:block:select', (event) => {
        const index = this.slides.findIndex((slide) => !slide.hasAttribute('data-loop-copy') && slide.dataset.blockId === event.detail.blockId);
        if (index !== -1) this.goTo(index);
      }, options);
      this.resize = new ResizeObserver(() => this.goTo(Math.max(this.active, 0), false));
      this.resize.observe(this.track);
      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.syncPlayback();
      }, { threshold: .25 });
      this.observer.observe(this);
      this.goTo(this.loopStart + Math.floor(this.originals.length / 2), false);
      this.update();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.resize?.disconnect();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      clearTimeout(this.settleTimer);
      this.querySelectorAll('video').forEach((video) => video.pause());
      this.youtubeVideos?.forEach((video) => video.destroy());
      this.youtubeVideos?.clear();
    }

    buildLoop() {
      this.loopStart = 0;
      if (this.originals.length < 2) return;
      const width = this.originals[0].getBoundingClientRect().width;
      const gap = parseFloat(getComputedStyle(this.track).columnGap);
      const copiesPerSide = Math.max(2, Math.ceil(Math.max(this.track.clientWidth, screen.width) / ((width + gap) * this.originals.length)) + 1);
      const before = document.createDocumentFragment();
      const after = document.createDocumentFragment();
      for (let copy = 0; copy < copiesPerSide; copy++) {
        this.originals.forEach((original) => {
          [before, after].forEach((fragment) => {
            const slide = original.cloneNode(true);
            slide.setAttribute('data-loop-copy', '');
            slide.removeAttribute('data-shopify-editor-block');
            slide.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
            fragment.append(slide);
          });
        });
      }
      this.track.prepend(before);
      this.track.append(after);
      this.loopStart = copiesPerSide * this.originals.length;
      this.slides = [...this.track.querySelectorAll('.social-videos__slide')];
    }

    recenter() {
      if (this.drag || this.originals.length < 2 || this.active < 0) return;
      const target = this.loopStart + this.active % this.originals.length;
      if (target === this.active) return;
      const current = this.slides[this.active];
      const replacement = this.slides[target];
      let video = this.getMedia(current);
      if (video instanceof YouTubeVideo) {
        const other = replacement.querySelector('[data-youtube-url]');
        const marker = document.createTextNode('');
        video.element.replaceWith(marker);
        other.replaceWith(video.element);
        marker.replaceWith(other);
        this.getMedia(current)?.pause();
        this.updatePlayButton(video);
      }
      const nextVideo = this.getMedia(replacement);
      const wasPlaying = video && !video.paused;
      if (video instanceof HTMLVideoElement && nextVideo instanceof HTMLVideoElement) {
        nextVideo.muted = this.muted;
        if (video.dataset.userPaused) nextVideo.dataset.userPaused = 'true';
        else delete nextVideo.dataset.userPaused;
        const time = video.currentTime;
        const restoreTime = () => { if (Number.isFinite(nextVideo.duration)) nextVideo.currentTime = Math.min(time, nextVideo.duration); };
        if (nextVideo.readyState) restoreTime();
        else nextVideo.addEventListener('loadedmetadata', restoreTime, { once: true, signal: this.abort.signal });
        this.updateMuteButton(nextVideo);
      }
      const shift = replacement.getBoundingClientRect().left - current.getBoundingClientRect().left;
      this.track.scrollTo({ left: this.track.scrollLeft + shift, behavior: 'instant' });
      this.update();
      if (wasPlaying && this.visible && !document.hidden) nextVideo.play().catch(() => {});
    }

    goTo(index, smooth = true) {
      const slide = this.slides[Math.max(0, Math.min(index, this.slides.length - 1))];
      const trackRect = this.track.getBoundingClientRect();
      const rect = slide.getBoundingClientRect();
      this.track.scrollTo({ left: this.track.scrollLeft + rect.left - trackRect.left + rect.width / 2 - this.track.clientWidth / 2, behavior: smooth && !this.reducedMotion.matches ? 'smooth' : 'instant' });
      this.schedule();
    }

    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); });
    }

    update() {
      const rect = this.track.getBoundingClientRect();
      const center = rect.left + this.track.clientWidth / 2;
      const gap = parseFloat(getComputedStyle(this.track).columnGap);
      let nearest = 0;
      let minimum = Infinity;
      this.slides.forEach((slide, index) => {
        const bounds = slide.getBoundingClientRect();
        const distance = Math.abs(bounds.left + bounds.width / 2 - center);
        slide.style.setProperty('--scale', Math.max(.8, 1 - distance / (bounds.width + gap) * .1));
        if (distance < minimum) { minimum = distance; nearest = index; }
      });
      if (nearest !== this.active) {
        this.active = nearest;
        this.slides.forEach((slide, index) => {
          slide.classList.toggle('is-active', index === nearest);
          slide.querySelectorAll('button, a').forEach((control) => { control.tabIndex = index === nearest ? 0 : -1; });
          if (index === nearest) this.updateMuteButton(this.getMedia(slide));
        });
        this.syncPlayback();
      }
      this.querySelectorAll('[data-direction]').forEach((button) => {
        button.disabled = this.originals.length < 2;
      });
    }

    getMedia(slide) {
      if (!slide) return null;
      const video = slide.querySelector('video');
      if (video) return video;
      const element = slide.querySelector('[data-youtube-url]');
      if (!element) return null;
      if (!this.youtubeVideos.has(element)) this.youtubeVideos.set(element, new YouTubeVideo(element, this));
      return this.youtubeVideos.get(element);
    }

    syncPlayback() {
      this.slides.forEach((slide, index) => {
        const video = this.getMedia(slide);
        if (!video) return;
        if (index !== this.active || !this.visible || document.hidden) video.pause();
      });
      const video = this.getMedia(this.slides[this.active]);
      if (!video) return;
      video.muted = this.muted;
      this.updateMuteButton(video);
      if (this.visible && !document.hidden && this.dataset.autoplay === 'true' && !this.reducedMotion.matches && !video.dataset.userPaused) video.play().catch(() => {});
    }

    setMuted(muted) {
      this.muted = muted;
      this.slides.forEach((slide) => {
        const video = this.getMedia(slide);
        if (!video) return;
        video.muted = muted;
        this.updateMuteButton(video);
      });
    }

    updatePlayButton(video) {
      if (!video) return;
      const button = video.parentElement.querySelector('[data-play]');
      button.classList.toggle('is-playing', !video.paused);
      button.setAttribute('aria-label', video.paused ? button.dataset.playLabel : button.dataset.pauseLabel);
    }

    updateMuteButton(video) {
      if (!video) return;
      const mute = video.parentElement.querySelector('[data-mute]');
      mute.setAttribute('aria-label', video.muted ? mute.dataset.unmuteLabel : mute.dataset.muteLabel);
      mute.setAttribute('aria-pressed', String(!video.muted));
      mute.querySelector('[data-sound-off]').toggleAttribute('hidden', !video.muted);
      mute.querySelector('[data-sound-on]').toggleAttribute('hidden', video.muted);
    }

    handleClick(event) {
      if (this.suppressClick) { this.suppressClick = false; event.preventDefault(); return; }
      const navigation = event.target.closest('[data-direction]');
      if (navigation) { this.goTo(this.active + Number(navigation.dataset.direction)); return; }
      const slide = event.target.closest('.social-videos__slide');
      if (!slide || event.target.closest('a')) return;
      const index = this.slides.indexOf(slide);
      if (index !== this.active) { this.goTo(index); return; }
      const video = this.getMedia(slide);
      if (!video) return;
      const mute = event.target.closest('[data-mute]');
      if (mute) {
        this.setMuted(!this.muted);
      } else if (event.target.closest('[data-play]')) {
        if (video.paused) { delete video.dataset.userPaused; video.play().catch(() => {}); }
        else { video.dataset.userPaused = 'true'; video.pause(); }
      }
    }
  });
}
